import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { api } from "@/lib/api";
import Avatar, { STATUS_COLORS, STATUS_LABELS } from "@/components/Avatar";
import { computePresenceSummary, isOnline as isEffectivelyOnline } from "@/lib/presence";
import {
  Home, Hash, MessageSquare, Search, Bell, Star,
  Truck, ListTodo, AlertTriangle, Sun, Building2, LogOut,
  Settings, ChevronDown, Plus, AtSign, Circle, Users,
  Lock, Briefcase, Inbox, UserPlus, MessageCircle, Sunrise,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ProfileCard from "@/components/ProfileCard";
import ProfileSettings from "@/components/ProfileSettings";
import AvatarSetup from "@/components/AvatarSetup";
import InvitePeopleDialog from "@/components/InvitePeopleDialog";
import FeedbackModal from "@/components/FeedbackModal";
import StatusPickerRich from "@/components/StatusPickerRich";

export default function LeftSidebar({ presence, connected, onWsSend, notifBump, subscribe, onOpenPeople, onInviteOpenRef }) {
  const { user, currentOrg, orgs, switchOrg, logout } = useAuth();
  const [channels, setChannels] = useState([]);
  const [dms, setDms] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [newChanOpen, setNewChanOpen] = useState(false);
  const [newDmOpen, setNewDmOpen] = useState(false);
  const [dmSearch, setDmSearch] = useState("");
  const [members, setMembers] = useState([]);
  const [openSections, setOpenSections] = useState({ channels: true, dms: true, collab: true, admin: true });
  const [profileOpen, setProfileOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const nav = useNavigate();

  // Expose the invite opener to parent (used by ActivationChecklist).
  useEffect(() => {
    if (onInviteOpenRef) onInviteOpenRef.current = () => setInviteOpen(true);
  }, [onInviteOpenRef]);

  const canInvite = ["owner", "admin", "manager"].includes(
    (orgs.find((o) => o.organization?.id === currentOrg?.id)?.membership?.role) || "member"
  );

  const toggle = (k) => setOpenSections((s) => ({ ...s, [k]: !s[k] }));

  useEffect(() => {
    if (!currentOrg) return;
    api.get("/channels").then((r) => setChannels(r.data));
    api.get("/conversations").then((r) => setDms(r.data));
    api.get("/notifications").then((r) => setNotifCount(r.data.unread_count));
    api.get("/orgs/current/members").then((r) => setMembers(r.data));
  }, [currentOrg, notifBump]);

  // Refresh channels / dms when any relevant WS event fires. This is the
  // mechanism that clears the unread badge the moment a channel is opened.
  useEffect(() => {
    if (!subscribe || !currentOrg) return;
    return subscribe((type, payload) => {
      if (type === "channel.read" || type === "message.new" || type === "message.deleted") {
        api.get("/channels").then((r) => setChannels(r.data)).catch(() => {});
      }
      if (type === "conversation.read" || type === "dm.new") {
        api.get("/conversations").then((r) => setDms(r.data)).catch(() => {});
      }
      if (type === "user.updated") {
        api.get("/orgs/current/members").then((r) => setMembers(r.data)).catch(() => {});
      }
      if (type === "unread.summary" && payload?.summary) {
        // The unread.summary event is org-wide; only apply the totals that
        // belong to the current user. Without this filter Alex sees Sarah's
        // count when Sarah marks something read.
        if (!payload.user_id || payload.user_id === user?.id) {
          setNotifCount(payload.summary.notifications_total ?? 0);
        }
      }
    });
  }, [subscribe, currentOrg, user?.id]);

  // Same-tab pub/sub fallback: NotificationsView emits `tovra:notif-unread`
  // right after a mark-read/mark-all call, so the badge updates instantly
  // even if the WS `unread.summary` handler misses the beat (iter21 bug).
  useEffect(() => {
    const onNotifUnread = (e) => {
      const c = e?.detail?.count;
      if (typeof c === "number") setNotifCount(c);
    };
    window.addEventListener("tovra:notif-unread", onNotifUnread);
    return () => window.removeEventListener("tovra:notif-unread", onNotifUnread);
  }, []);

  const createChannel = async (fd) => {
    try {
      await api.post("/channels", fd);
      const { data } = await api.get("/channels");
      setChannels(data);
      setNewChanOpen(false);
      toast.success("Channel created");
    } catch { toast.error("Failed"); }
  };

  const startDm = async (uid) => {
    const { data } = await api.post("/conversations", { user_ids: [uid] });
    const { data: convs } = await api.get("/conversations");
    setDms(convs);
    setNewDmOpen(false);
    nav(`/app/dm/${data.id}`);
  };

  const myPresence = presence?.[user?.id];
  const currentStatus = isEffectivelyOnline(myPresence)
    ? (myPresence.status || "available")
    : (myPresence?.online ? "available" : (user?.status && user.status !== "offline" ? user.status : "available"));
  const myOnline = isEffectivelyOnline(myPresence);
  const onlineCount = useMemo(
    () => computePresenceSummary(presence, members).online,
    [presence, members]
  );
  const otherMembers = useMemo(() => members.filter((m) => m.user_id !== user?.id), [members, user?.id]);

  return (
    <aside className="w-64 bg-[var(--sidebar-bg)] text-[var(--sidebar-text)] flex flex-col shrink-0 border-r border-black/40 noise-overlay">
      {/* Org switcher */}
      <Popover>
        <PopoverTrigger asChild>
          <button className="h-14 px-4 flex items-center gap-2 border-b border-white/5 hover:bg-white/5" data-testid="org-switcher">
            <div className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold shrink-0"
              style={{ background: currentOrg?.accent_color || "var(--brand-accent)" }}>
              {currentOrg?.name?.[0] || "?"}
            </div>
            <div className="flex-1 text-left overflow-hidden">
              <div className="text-white text-sm font-semibold truncate">{currentOrg?.name || "…"}</div>
              <div className="text-[9px] text-zinc-500 tracking-widest uppercase flex items-center gap-1">
                <Circle className={`w-1.5 h-1.5 ${connected ? "fill-emerald-400 text-emerald-400" : "fill-zinc-500 text-zinc-500"}`} />
                {connected ? `${onlineCount} ONLINE · POWERED BY TOVRA` : "OFFLINE · POWERED BY TOVRA"}
              </div>
            </div>
            <ChevronDown className="w-4 h-4 shrink-0"/>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-1">
          <div className="text-xs text-zinc-500 px-2 pt-1 pb-2">Workspaces</div>
          {orgs.map((o) => (
            <button
              key={o.organization.id}
              data-testid={`org-option-${o.organization.slug}`}
              onClick={() => { switchOrg(o.organization.id); window.location.reload(); }}
              className={`w-full text-left px-2 py-2 rounded-md hover:bg-zinc-100 flex items-center gap-2 ${o.organization.id === currentOrg?.id ? "bg-zinc-50" : ""}`}
            >
              <div className="w-6 h-6 rounded bg-zinc-800 text-white text-xs flex items-center justify-center">{o.organization.name[0]}</div>
              <div className="text-sm">{o.organization.name}</div>
            </button>
          ))}
          <button onClick={() => nav("/onboarding")} className="w-full text-left px-2 py-2 rounded-md hover:bg-zinc-100 flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> New workspace
          </button>
          {canInvite && (
            <>
              <div className="my-1 border-t border-zinc-100" />
              <button
                onClick={() => setInviteOpen(true)}
                data-testid="workspace-menu-invite"
                className="w-full text-left px-2 py-2 rounded-md hover:bg-zinc-100 flex items-center gap-2 text-sm"
              >
                <UserPlus className="w-4 h-4 text-[var(--brand-accent)]" /> Invite people
              </button>
            </>
          )}
          <div className="my-1 border-t border-zinc-100" />
          <button
            onClick={() => setFeedbackOpen(true)}
            data-testid="workspace-menu-feedback"
            className="w-full text-left px-2 py-2 rounded-md hover:bg-zinc-100 flex items-center gap-2 text-sm"
          >
            <MessageCircle className="w-4 h-4 text-zinc-500" /> Send feedback
          </button>
        </PopoverContent>
      </Popover>

      {/* User row */}
      <div className="px-4 py-3 border-b border-white/5 flex items-center gap-3">
        <ProfileCard
          user={user}
          presence={myPresence}
          onEditProfile={(section) => section === "avatar" ? setAvatarOpen(true) : setProfileOpen(true)}
        >
          <button className="cursor-pointer" aria-label="Your profile" data-testid="current-user-avatar-btn">
            <Avatar user={user} size="lg" status={currentStatus} online={myOnline} showStatus/>
          </button>
        </ProfileCard>
        <div className="flex-1 leading-tight overflow-hidden">
          <div className="text-white text-sm font-medium truncate">{user?.name}</div>
          <StatusPickerRich
            currentStatus={currentStatus}
            statusText={myPresence?.status_text}
            focusUntil={myPresence?.focus_until}
            onOptimistic={(patch) => {
              // Nudge the WS so other tabs see it immediately; server WS
              // broadcast is authoritative and will land within ~ms.
              onWsSend && onWsSend({ type: "presence.update", ...patch });
            }}
          >
            <button
              className="text-[11px] text-zinc-400 hover:text-white flex items-center gap-1 max-w-full"
              data-testid="status-picker"
              title={myPresence?.status_text || STATUS_LABELS[currentStatus]}
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_COLORS[currentStatus] }}/>
              <span className="truncate">
                {myPresence?.status_text || STATUS_LABELS[currentStatus] || "Set status"}
              </span>
            </button>
          </StatusPickerRich>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto scroll-thin py-2" aria-label="Primary">
        <NavItem to="/app/home" icon={Home} label="Home" testid="nav-home" />
        <NavItem to="/app/office" icon={Building2} label="Virtual office" testid="nav-office" />
        <button onClick={onOpenPeople} className="flex items-center gap-2.5 px-4 py-2 text-sm hover:bg-white/5 w-full text-left" data-testid="nav-people">
          <Users className="w-4 h-4 opacity-70"/>
          <span className="flex-1">People</span>
          <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-white/10 text-white">{onlineCount}</span>
        </button>
        <NavItem to="/app/mentions" icon={AtSign} label="Mentions" testid="nav-mentions" />
        <NavItem to="/app/saved" icon={Star} label="Saved" testid="nav-saved" />
        <NavItem to="/app/search" icon={Search} label="Search" testid="nav-search" />
        <NavItem to="/app/notifications" icon={Bell} label="Notifications" badge={notifCount} testid="nav-notifications" />

        <SectionHeader label="Channels" open={openSections.channels} onToggle={() => toggle("channels")}
          action={(
            <Dialog open={newChanOpen} onOpenChange={setNewChanOpen}>
              <DialogTrigger asChild>
                <button className="text-zinc-500 hover:text-white" data-testid="new-channel-btn" aria-label="New channel"><Plus className="w-3.5 h-3.5"/></button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New channel</DialogTitle></DialogHeader>
                <NewChannelForm onCreate={createChannel} currentUserId={user?.id}/>
              </DialogContent>
            </Dialog>
          )}
        />
        {openSections.channels && channels.map((c) => (
          <NavLink key={c.id} to={`/app/channel/${c.id}`} data-testid={`channel-${c.name}`}
            className={({ isActive }) => `flex items-center gap-2 px-4 py-1.5 text-sm hover:bg-white/5 ${isActive ? "bg-white/10 text-white" : ""}`}>
            {c.is_private ? <Lock className="w-3.5 h-3.5 opacity-60"/> : <Hash className="w-4 h-4 opacity-60"/>}
            <span className="flex-1 truncate">{c.name}</span>
            {c.unread > 0 && <span className="text-[10px] bg-[var(--brand-accent)] text-white rounded-full px-1.5 py-0.5">{c.unread}</span>}
          </NavLink>
        ))}

        <SectionHeader label="Direct Messages" open={openSections.dms} onToggle={() => toggle("dms")}
          action={(
            <Dialog open={newDmOpen} onOpenChange={(v) => { setNewDmOpen(v); if (!v) setDmSearch(""); }}>
              <DialogTrigger asChild>
                <button className="text-zinc-500 hover:text-white" data-testid="new-dm-btn" aria-label="New DM"><Plus className="w-3.5 h-3.5"/></button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Start a direct message</DialogTitle></DialogHeader>
                {/* Search filter (case-insensitive across name, title, department, email). */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none"/>
                  <Input
                    autoFocus
                    value={dmSearch}
                    onChange={(e) => setDmSearch(e.target.value)}
                    placeholder="Search by name, title, or department…"
                    className="pl-8"
                    data-testid="new-dm-search"
                  />
                </div>
                <div className="max-h-80 overflow-y-auto scroll-thin">
                  <DmMemberList
                    members={otherMembers}
                    search={dmSearch}
                    presence={presence}
                    onPick={(uid) => { startDm(uid); setDmSearch(""); }}
                  />
                </div>
              </DialogContent>
            </Dialog>
          )}
        />
        {openSections.dms && dms.map((c) => {
          const other = c.members?.find((m) => m?.id !== user?.id) || c.members?.[0];
          const status = presence?.[other?.id]?.status || other?.status;
          const online = isEffectivelyOnline(presence?.[other?.id]);
          return (
            <NavLink key={c.id} to={`/app/dm/${c.id}`} data-testid={`dm-${c.id}`}
              className={({ isActive }) => `flex items-center gap-2 px-4 py-1.5 text-sm hover:bg-white/5 ${isActive ? "bg-white/10 text-white" : ""}`}>
              <Avatar user={other} size="sm" status={status} online={online}/>
              <span className="flex-1 truncate">{other?.name || "You"}</span>
            </NavLink>
          );
        })}

        <SectionHeader label="Collaboration" open={openSections.collab} onToggle={() => toggle("collab")}/>
        {openSections.collab && (
          <>
            <NavItem to="/app/projects" icon={Briefcase} label="Projects" testid="nav-projects" />
            <NavItem to="/app/tasks" icon={ListTodo} label="Tasks" testid="nav-tasks" />
            <NavItem to="/app/standup" icon={Sunrise} label="Daily stand-up" testid="nav-standup" />
            <NavItem to="/app/requests" icon={Inbox} label="Requests" testid="nav-requests" />
          </>
        )}

        <SectionHeader label="Administration" open={openSections.admin} onToggle={() => toggle("admin")}/>
        {openSections.admin && <NavItem to="/app/admin" icon={Settings} label="Admin" testid="nav-admin" />}
        {openSections.admin && currentOrg?.internal_workspace && (
          <NavItem to="/app/beta" icon={Circle} label="Internal beta" testid="nav-beta" />
        )}
        {openSections.admin && currentOrg?.internal_workspace && (
          <NavItem to="/app/waitlist" icon={Circle} label="Waitlist leads" testid="nav-waitlist" />
        )}
        {openSections.admin && (
          <NavItem to="/app/departments" icon={Circle} label="Departments" testid="nav-departments" />
        )}
        {openSections.admin && <NavItem to="/app/settings/office-studio" icon={Building2} label="Office Studio" testid="nav-office-studio" />}
      </nav>

      <div className="border-t border-white/5 flex items-center">
        <button onClick={() => setProfileOpen(true)} data-testid="open-profile-settings" className="flex-1 h-12 px-4 flex items-center gap-2 text-sm hover:bg-white/5">
          <Settings className="w-4 h-4" /> Profile settings
        </button>
        <button onClick={logout} data-testid="logout-btn" className="h-12 px-4 flex items-center gap-2 text-sm hover:bg-white/5 border-l border-white/5">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <ProfileSettings open={profileOpen} onOpenChange={setProfileOpen} />
      <InvitePeopleDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <Dialog open={avatarOpen} onOpenChange={setAvatarOpen}>
        <DialogContent className="max-w-3xl" aria-describedby="sidebar-avatar-desc">
          <DialogHeader>
            <DialogTitle>Personal avatar</DialogTitle>
            <DialogDescription id="sidebar-avatar-desc">
              Upload a photo or design an illustrated avatar. Your selection persists across every workspace view.
            </DialogDescription>
          </DialogHeader>
          <AvatarSetup onSaved={() => setAvatarOpen(false)} onCancel={() => setAvatarOpen(false)} />
        </DialogContent>
      </Dialog>
    </aside>
  );
}

function SectionHeader({ label, open, onToggle, action }) {
  return (
    <div className="mt-4 px-4 text-[10px] tracking-widest text-zinc-500 uppercase flex items-center justify-between">
      <button onClick={onToggle} className="flex items-center gap-1.5 hover:text-zinc-300">
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "" : "-rotate-90"}`}/>
        <span>{label}</span>
      </button>
      {action}
    </div>
  );
}

function NavItem({ to, icon: Icon, label, badge, testid }) {
  return (
    <NavLink to={to} data-testid={testid}
      className={({ isActive }) => `flex items-center gap-2.5 px-4 py-2 text-sm hover:bg-white/5 ${isActive ? "bg-white/10 text-white" : ""}`}>
      <Icon className="w-4 h-4 opacity-70" />
      <span className="flex-1">{label}</span>
      {badge > 0 && <span className="text-[10px] bg-[var(--brand-accent)] text-white rounded-full px-1.5 py-0.5">{badge}</span>}
    </NavLink>
  );
}

function NewChannelForm({ onCreate, currentUserId }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priv, setPriv] = useState(false);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]); // user_ids
  const [busy, setBusy] = useState(false);
  // Filter out self once per members/currentUserId change to avoid the
  // per-render `.filter().map()` chain in JSX.
  const invitableMembers = useMemo(
    () => members.filter((m) => m.user_id !== currentUserId),
    [members, currentUserId]
  );

  useEffect(() => {
    api.get("/orgs/current/members")
      .then(({ data }) => setMembers(data || []))
      .catch(() => {});
  }, []);

  const toggle = (uid) => {
    setSelected((s) => (s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]));
  };

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onCreate({
        name,
        description,
        is_private: priv,
        member_ids: priv ? selected : [],
      });
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = name.trim() && (!priv || selected.length > 0);

  return (
    <div className="space-y-3">
      <Input placeholder="channel-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-chan-name"/>
      <Input placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="new-chan-desc"/>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={priv} onChange={(e) => setPriv(e.target.checked)} data-testid="new-chan-private"/>
        <span>Private channel <span className="text-zinc-400">— only invited members can see it</span></span>
      </label>
      {priv && (
        <div className="border border-zinc-200 rounded-lg overflow-hidden" data-testid="new-chan-members-picker">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold border-b border-zinc-100 bg-zinc-50 flex items-center justify-between">
            <span>Invite members</span>
            <span className="tabular-nums text-zinc-600">{selected.length} selected</span>
          </div>
          <div className="max-h-52 overflow-y-auto scroll-thin">
            {invitableMembers
              .map((m) => {
                const on = selected.includes(m.user_id);
                return (
                  <button
                    key={m.user_id}
                    type="button"
                    onClick={() => toggle(m.user_id)}
                    data-testid={`new-chan-member-${m.user_id}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition ${on ? "bg-orange-50" : "hover:bg-zinc-50"}`}
                  >
                    <input type="checkbox" checked={on} readOnly className="pointer-events-none"/>
                    <Avatar user={m.user} size="sm"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-zinc-900 truncate">{m.user?.name || m.user?.email}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{m.department || m.role || m.user?.email}</div>
                    </div>
                  </button>
                );
              })}
          </div>
        </div>
      )}
      <Button
        className="w-full bg-[var(--brand-primary)] text-white disabled:opacity-50"
        disabled={!canSubmit || busy}
        onClick={submit}
        data-testid="new-chan-submit"
      >
        {busy ? "Creating…" : "Create channel"}
      </Button>
    </div>
  );
}

/** Filtered member list for the DM picker — extracted so it isn't redefined on every render. */
function DmMemberList({ members, search, presence, onPick }) {
  const q = search.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return members;
    return members.filter((m) => {
      const hay = [
        m.user?.name, m.user?.title, m.user?.email, m.department, m.role,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [members, q]);

  if (matches.length === 0) {
    return (
      <div className="text-sm text-zinc-500 text-center py-6" data-testid="new-dm-empty">
        No people match &quot;{search}&quot;.
      </div>
    );
  }
  return matches.map((m) => (
    <button
      key={m.user_id}
      onClick={() => onPick(m.user_id)}
      data-testid={`dm-user-${m.user_id}`}
      className="w-full text-left px-3 py-2 rounded hover:bg-zinc-100 flex items-center gap-3"
    >
      <Avatar
        user={m.user}
        size="md"
        status={presence?.[m.user_id]?.status || m.user?.status}
        online={isEffectivelyOnline(presence?.[m.user_id])}
      />
      <div>
        <div className="text-sm">{m.user?.name}</div>
        <div className="text-xs text-zinc-500">{m.user?.title} · {m.department}</div>
      </div>
    </button>
  ));
}

